import React from "react";
import { useInstantSearch } from "react-instantsearch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTag } from "@fortawesome/free-solid-svg-icons";

// Labels pour les champs de matching
const FIELD_LABELS = {
  titre: "Titre",
  ex_titre: "Titre séquence",
  ex_cote: "Cote exemplaire",
  ex_cote_s: "Cotes",
  sous_titre: "Sous-titre",
  analyse: "Analyse",
  contient: "Contenu",
  sujet: "Sujet",
  auteur: "Auteur",
  notes: "Notes",
  emission: "Émission",
  lieu: "Lieu",
  fonds: "Fonds",
  full_text: "Texte intégral"
};

// Champs à ne JAMAIS afficher dans les matchings
const HIDDEN_FIELDS = new Set(["full_text"]);

// Champs déjà affichés en fixe (on ne les répète pas dans les matchings)
const FIXED_FIELDS = new Set([
  "titre", "auteur", "fonds", "sujet"
]);

// Extrait le label d'une valeur "idref|Label"
function extractLabel(value) {
  if (!value) return "";
  const parts = value.split("|");
  return parts[1] || parts[0];
}

// Champs dont on affiche TOUTES les valeurs (vocabulaire court) au lieu d'un seul extrait
const FULL_VALUE_FIELDS = new Set(["analyse", "genre", "lieu", "emission"]);

// Échappe le HTML d'une valeur brute (ces valeurs sont injectées via dangerouslySetInnerHTML)
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Surligne (<em>) les mots recherchés dans un texte brut (texte échappé au préalable, un seul passage)
function highlightWords(text, words) {
  let out = escapeHtml(text);
  const list = (words || []).filter(Boolean);
  if (list.length === 0) return out;
  const alts = list
    .map((w) => escapeHtml(w).replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .sort((a, b) => b.length - a.length);
  try {
    out = out.replace(new RegExp("(" + alts.join("|") + ")", "gi"), "<em>$1</em>");
  } catch (e) {
    /* motif invalide : on renvoie le texte brut */
  }
  return out;
}

// Nettoie une valeur brute : décode les entités HTML (ex: &#34; → " ) et retire les balises résiduelles
function cleanText(value) {
  let s = String(value == null ? "" : value);
  if (typeof document !== "undefined") {
    const ta = document.createElement("textarea");
    ta.innerHTML = s;
    s = ta.value; // décodage des entités HTML
  }
  s = s
    .replace(/<\s*br\s*\/?\s*>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return s;
}

// Nettoie un extrait surligné par OpenSearch : décode les entités (&#34; → ", &lt;br/&gt; → espace)
// tout en conservant les balises <em> du surlignage.
function cleanFragment(html) {
  if (!html) return "";
  const EM_OPEN = "\u0001";
  const EM_CLOSE = "\u0002";
  let s = String(html).replace(/<em>/gi, EM_OPEN).replace(/<\/em>/gi, EM_CLOSE);
  if (typeof document !== "undefined") {
    const ta = document.createElement("textarea");
    ta.innerHTML = s;
    s = ta.value; // décodage des entités HTML
  }
  s = s
    .replace(/<\s*br\s*\/?\s*>/gi, " ")
    .replace(/<\s*\/?\s*(p|div|span|strong|b|i|u|ul|ol|li|table|tr|td|th|h[1-6])\b[^>]*>/gi, " ")
    .replace(/<\s*(script|style)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, " ")
    .replace(/\s{2,}/g, " ");
  return s.split(EM_OPEN).join("<em>").split(EM_CLOSE).join("</em>");
}

// Étiquette cliquable invisible — filtre sans changer le rendu visuel
function InlineFilterLabel({ value, attribute, children, setIndexUiState }) {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIndexUiState((prev) => {
      const currentList = prev.refinementList?.[attribute] || [];
      const nextList = currentList.includes(value)
        ? currentList.filter((v) => v !== value)
        : [...currentList, value];
      return {
        ...prev,
        refinementList: {
          ...prev.refinementList,
          [attribute]: nextList.length > 0 ? nextList : undefined,
        },
      };
    });
  };

  return (
    <span
      onClick={handleClick}
      title={"Filtrer par " + attribute + " : " + value}
      className="sui-result-tag-inline"
    >
      {children}
    </span>
  );
}

// Étiquette cliquable pour genre/sujet
function TagLabel({ value, attribute, label: fieldLabel, className, setIndexUiState }) {
  const display = extractLabel(value);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIndexUiState((prev) => {
      const currentList = prev.refinementList?.[attribute] || [];
      const nextList = currentList.includes(value)
        ? currentList.filter((v) => v !== value)
        : [...currentList, value];
      return {
        ...prev,
        refinementList: {
          ...prev.refinementList,
          [attribute]: nextList.length > 0 ? nextList : undefined,
        },
      };
    });
  };

  return (
    <span
      className={"sui-result-tag " + (className || "")}
      onClick={handleClick}
      title={"Ajouter le filtre : " + display}
    >
      <FontAwesomeIcon icon={faTag} className="sui-result-tag-icon" />
      {display}
    </span>
  );
}

export default function ResultView({ hit }) {
  const { setIndexUiState } = useInstantSearch();

  // Récupération des highlights ou fallbacks
  const titreRaw = hit._highlightResult?.titre;
  const titre = (Array.isArray(titreRaw) ? titreRaw[0]?.value : titreRaw?.value) || hit.titre || "";
  const dateComplete = hit.date_complete || "";
  const url = hit.urlplus || "#";
  const doctype = hit.doctype || hit.docType || "";
  const imagette = hit.imagette ? decodeURIComponent(hit.imagette) : "";

  const auteurBrut = hit.auteur || ""; // pour le filtre
  const auteurHtml = hit._highlightResult?.auteur?.text?.value || auteurBrut;
  const auteurRole = hit.auteurRole || "";
  const collation = hit.collation || "";
  const cote = hit.cote || "";
  const fonds = hit._highlightResult?.fonds?.value || hit.fonds || "";
  const access = hit.access || "";
  const corrigeDate = hit.corrigeDate || "";

  let dateModif = "";
  if (corrigeDate) {
    const d = new Date(corrigeDate * 1000);
    dateModif = d.toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
  }

  // Champs multivalués : genre et sujet (tableaux d'idref|Label)
  const genres = hit.genre || [];
  const sujets = hit.sujet || [];

  // Champs matchés par la recherche (non déjà affichés en fixe)
  const matchedFields = [];
  if (hit._highlightResult) {
    for (const [field, h] of Object.entries(hit._highlightResult)) {
      const baseField = field.replace(/\.text$/, "");
      if (FIXED_FIELDS.has(baseField)) continue;
      if (HIDDEN_FIELDS.has(baseField)) continue;

      // Cas simple : {value, matchedWords} (champ texte simple → extrait fourni par OpenSearch)
      if (h.matchedWords && h.matchedWords.length > 0 && h.value) {
        matchedFields.push({ field: baseField, label: FIELD_LABELS[baseField] || baseField, html: cleanFragment(h.value) });
        continue;
      }

      // Cas multi-field keyword→text multi-valué : {text: [{value, matchedWords}, ...]}
      if (Array.isArray(h.text)) {
        const matched = h.text.filter((el) => el.matchedWords && el.matchedWords.length > 0);
        if (matched.length === 0) continue;

        let html;
        if (FULL_VALUE_FIELDS.has(baseField)) {
          // Vocabulaire court (analyse, genre, lieu, emission) → afficher TOUTES les valeurs du champ
          const raw = hit[baseField];
          const values = Array.isArray(raw) ? raw : (raw != null && raw !== "" ? [raw] : []);
          const src = values.length > 0 ? values : matched.map((el) => el.value);
          const words = matched.flatMap((el) => el.matchedWords || []);
          html = src.map((v) => highlightWords(cleanText(extractLabel(String(v))), words)).join(", ");
        } else {
          // Autres champs multi-valués (ex: ex_cote) → toutes les valeurs matchées
          html = matched.map((el) => cleanFragment(el.value)).join(", ");
        }

        matchedFields.push({ field: baseField, label: FIELD_LABELS[baseField] || baseField, html });
      }
    }
  }

  return (
    <div className="sui-result">
      <div style={{ color: "#4f4f4f", position: "relative" }}>
        {doctype === "k" && (
          <img
            src={'https://archives.memovs.ch/images/icone-k.png'}
            className="sui-result__doctype"
            alt=""
            style={{ float: "right", objectFit: "cover", objectPosition: "center" }}
          />
        )}
        {doctype === "v" && (
          <img
            src={'https://archives.memovs.ch/images/icone-v.png'}
            className="sui-result__doctype"
            alt=""
            style={{ float: "right", objectFit: "cover", objectPosition: "center" }}
          />
        )}
        {doctype === "s" && (
          <img
            src={'https://archives.memovs.ch/images/icone-s.png'}
            className="sui-result__doctype"
            alt=""
            style={{ float: "right", objectFit: "cover", objectPosition: "center" }}
          />
        )}

        <div className="sui-result__header">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <div
              className="sui-result__title"
              title={hit.titre || ""}
              dangerouslySetInnerHTML={{ __html: titre + (dateComplete ? ", " + dateComplete : "") }}
            />
          </a>
        </div>
        <div className="sui-result__body">
          {imagette && (
            <div
              style={{ maxWidth: "140px", paddingLeft: "24px", paddingTop: "10px" }}
              className="sui-result__imgcontent"
            >
              <a href={url} target="_blank" rel="noopener noreferrer">
                <img
                  className="sui-result__image"
                  src={imagette}
                  alt="thumb"
                  style={{ display: "block", width: "100%", objectFit: "cover", objectPosition: "center" }}
                />
              </a>
            </div>
          )}

          <ul className="sui-result__details">
            {auteurBrut && (
              <li>
                <InlineFilterLabel value={auteurBrut} attribute="auteur" setIndexUiState={setIndexUiState}>
                  <span className="sui-result__value">
                    <span style={{ fontSize: "1.1em", color: "#272626" }} dangerouslySetInnerHTML={{ __html: "<b>" + auteurHtml + "</b>" }} />
                    {auteurRole && <span style={{ fontSize: "0.85em", color: "#888" }}> ({auteurRole})</span>}
                  </span>
                </InlineFilterLabel>
              </li>
            )}
            {collation && (
              <li>
                <span className="sui-result__value">{collation}</span>
              </li>
            )}
            {fonds && (
              <li style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "baseline" }}>
                <span style={{ fontSize: "1em", color: "#aaa" }}>Fonds</span>
                <TagLabel value={fonds} attribute="fonds" label="Fonds" setIndexUiState={setIndexUiState} />
              </li>
            )}

            {/* Étiquettes genre */}
            {genres.length > 0 && (
              <li style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "baseline" }}>
                <span style={{ fontSize: "1em", color: "#aaa" }}>Genre / Forme</span>
                {genres.map((g, i) => (
                  <TagLabel key={i} value={g} attribute="genre" label="Genre / Forme" setIndexUiState={setIndexUiState} />
                ))}
              </li>
            )}

            {/* Étiquettes sujet */}
            {sujets.length > 0 && (
              <li style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "baseline" }}>
                <span style={{ fontSize: "1em", color: "#aaa" }}>Sujet</span>
                {sujets.map((s, i) => (
                  <TagLabel key={i} value={s} attribute="sujet" label="Sujet" setIndexUiState={setIndexUiState} />
                ))}
              </li>
            )}

            {cote && (
              <li>
                <span className="sui-result__value coteSnippet sui-result__surplus" dangerouslySetInnerHTML={{ __html: cote }} />
              </li>
            )}            
            {access === "MV" && (
              <li className="sui-result__value" style={{ color: "#CC0000" }}>
                <i>Document consultable uniquement depuis la Médiathèque Valais</i>
              </li>
            )}

            {/* Champs matchés par la recherche non affichés en fixe */}
            {matchedFields.map(({ field, label, html }) => (
              <li key={field} className="sui-result__value sui-result__surplus" style={{ fontSize: "13px" }}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: "<b>" + label + " :</b>&nbsp;" + html
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
        {dateModif && (
          <div style={{ textAlign: "right", fontSize: "11px", color: "#bbb", marginTop: "8px" }}>
            Modifié le {dateModif}
          </div>
        )}
      </div>
    </div>
  );
}